import ApiService from "./api.js";
import eventBus from "./eventBus.js";

class AuthenticationService {
  constructor() {
    this.token = localStorage.getItem("authToken");
    this.user = JSON.parse(localStorage.getItem("user") || "null");
    this.isAuthenticated = !!this.token;
    this.permissions = new Set(
      JSON.parse(localStorage.getItem("permissions") || "[]"),
    );
    this.permissionsVersion =
      localStorage.getItem("permissionsVersion") || null;

    // Also load userId from localStorage if available (used for agenda API calls)
    this.userId = localStorage.getItem("userId");
  }

  // Emit a DOM event so other UI components (non-reactive singletons) can update
  _notifyUserUpdated() {
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.dispatchEvent === "function"
      ) {
        window.dispatchEvent(
          new CustomEvent("auth:user-updated", { detail: this.user }),
        );
      }
      // Also emit eventBus event for reactive Vue components
      eventBus.emit("user-updated", this.user);
    } catch (e) {
      // ignore
    }
  }

  // Login gebruiker
  async login(credentials) {
    try {
      const response = await ApiService.request("/auth/login", {
        method: "POST",
        body: JSON.stringify(credentials),
      });

      if (response.success) {
        this.token = response.data.token;
        this.user = response.data.user;
        this.isAuthenticated = true;

        // Sla op in localStorage
        localStorage.setItem("authToken", this.token);
        localStorage.setItem("user", JSON.stringify(this.user));

        // Save userId for agenda API calls (MongoDB uses _id)
        const userId = this.user._id || this.user.id;
        if (userId) {
          localStorage.setItem("userId", userId);
        }

        // notify other components (sidebar, etc.) that user changed
        this._notifyUserUpdated();

        // Zet token voor toekomstige API calls
        ApiService.setAuthToken(this.token);

        // Refresh profile and permissions from server to ensure normalized fields (department {id,name})
        try {
          await this.getCurrentUser();
        } catch (err) {
          // fallback: try to fetch permissions alone
          try {
            await this.fetchPermissions();
          } catch (e) {
            /* ignore */
          }
        }

        return {
          success: true,
          user: this.user,
          message: response.message,
        };
      }

      return {
        success: false,
        error: response.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Login gefaald",
      };
    }
  }

  // Logout gebruiker
  async logout() {
    try {
      if (this.token) {
        // Probeer server-side logout
        await ApiService.request("/auth/logout", {
          method: "POST",
        });
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      // Clear local state altijd
      this.clearAuthData();
    }
  }

  // Clear authentication data
  clearAuthData() {
    this.token = null;
    this.user = null;
    this.userId = null;
    this.isAuthenticated = false;

    this.permissions = new Set();
    this.permissionsVersion = null;

    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("userId");
    localStorage.removeItem("permissions");
    localStorage.removeItem("permissionsVersion");

    ApiService.clearAuthToken();
  }

  // Get current user ID, ensuring it's loaded first
  async getUserId() {
    // If we already have the user and ID, return it
    // Note: MongoDB uses _id, not id!
    if (this.user && (this.user.id || this.user._id)) {
      const userId = this.user.id || this.user._id;
      return userId;
    }

    // If not, try to refresh the current user
    if (this.isAuthenticated) {
      try {
        const user = await this.getCurrentUser();
        if (user && (user.id || user._id)) {
          const userId = user.id || user._id;
          return userId;
        }
      } catch (err) {
        console.error("Failed to get current user:", err);
      }
    }

    return null;
  }

  // Haal huidige gebruiker profiel op
  async getCurrentUser() {
    if (!this.isAuthenticated) {
      return null;
    }

    try {
      const response = await ApiService.request("/auth/me");

      if (response.success) {
        this.user = response.data.user;
        localStorage.setItem("user", JSON.stringify(this.user));
        // Refresh permissions when we refresh the profile
        try {
          await this.fetchPermissions();
        } catch (err) {
          // Ignore permission refresh errors
        }
        this._notifyUserUpdated();
        return this.user;
      }

      return null;
    } catch (error) {
      console.error("Get current user failed:", error);
      // Als token invalid is, log uit
      if (error.message.includes("401") || error.message.includes("403")) {
        this.clearAuthData();
      }
      return null;
    }
  }

  // Check of gebruiker specifieke rol heeft
  hasRole(role) {
    if (!this.user || !this.user.role) return false;
    const normalizeRole = (roleVal) => {
      if (!roleVal) return "";
      const r = String(roleVal).toLowerCase();
      if (r === "admin") return "administrator";
      if (r === "user" || r === "medewerker" || r === "employee")
        return "employee";
      if (r === "manager") return "manager";
      if (r === "intern" || r === "stagair" || r === "stagiair")
        return "intern";
      // if already canonical, return as-is
      return r;
    };

    const userRole = normalizeRole(this.user.role);
    const wanted = normalizeRole(role);
    return userRole === wanted;
  }

  // Check of gebruiker een van de rollen heeft
  hasAnyRole(roles) {
    // Role checks disabled in frontend for development — everyone 'has' any role.
    return true;
  }

  // Check of gebruiker admin is
  isAdmin() {
    return this.hasRole("administrator");
  }

  // Check of gebruiker manager is
  isManager() {
    return this.hasRole("manager");
  }

  // Check of gebruiker intern is
  isIntern() {
    return this.hasRole("intern");
  }

  // Get user display name
  getUserDisplayName() {
    if (!this.user) return "Onbekend";

    if (this.user.firstName && this.user.lastName) {
      return `${this.user.firstName} ${this.user.lastName}`;
    }

    return this.user.username || this.user.email;
  }

  // Initialize auth state (call on app start)
  async initialize() {
    if (this.token) {
      ApiService.setAuthToken(this.token);

      // Verifieer token door profiel op te halen
      const user = await this.getCurrentUser();
      if (!user) {
        // Token is invalid, clear data
        this.clearAuthData();
        return false;
      }

      // Ensure permissions are loaded; fetchPermissions called in getCurrentUser
      return true;
    }

    return false;
  }

  // Fetch effective permissions for the current authenticated user
  async fetchPermissions() {
    if (!this.isAuthenticated || !this.token)
      throw new Error("Not authenticated");
    try {
      const response = await ApiService.request("/auth/me/permissions");
      if (!response || !response.success) {
        throw new Error(
          response && response.error
            ? response.error
            : "Failed to fetch permissions",
        );
      }

      const data = response.data || {};
      const perms = Array.isArray(data.permissions) ? data.permissions : [];
      this.permissions = new Set(perms);
      this.permissionsVersion = data.version || null;

      localStorage.setItem("permissions", JSON.stringify(perms));
      if (this.permissionsVersion)
        localStorage.setItem("permissionsVersion", this.permissionsVersion);
      return { permissions: perms, version: this.permissionsVersion };
    } catch (err) {
      console.error("fetchPermissions error:", err);
      throw err;
    }
  }

  // Update current user's limited profile fields (PUT /api/auth/me)
  async updateProfile(data = {}) {
    if (!this.isAuthenticated) throw new Error("Not authenticated");
    try {
      const response = await (
        await import("./api.js")
      ).default.request("/auth/me", {
        method: "PUT",
        body: JSON.stringify(data),
      });

      if (response && response.success && response.data && response.data.user) {
        this.user = response.data.user;
        localStorage.setItem("user", JSON.stringify(this.user));
        // Refresh permissions as some profile changes might affect scope
        try {
          await this.fetchPermissions();
        } catch (e) {
          /* ignore */
        }
        this._notifyUserUpdated();
        return { success: true, user: this.user };
      }

      return {
        success: false,
        error: response && (response.error || response.message),
      };
    } catch (err) {
      console.error("updateProfile error:", err);
      return {
        success: false,
        error: err.message || "Failed to update profile",
      };
    }
  }

  // Upload profile avatar (FormData with field `avatar`)
  async uploadAvatar(file) {
    if (!this.isAuthenticated) throw new Error("Not authenticated");
    if (!file) throw new Error("No file provided");

    // Determine base URL the same way ApiService does
    const API_BASE_URL = import.meta.env.DEV
      ? "/api"
      : import.meta.env.VITE_API_BASE_URL ||
        "https://netnest-internal-platform-backend-2dow.onrender.com/api";

    const url = `${API_BASE_URL}/auth/me/avatar`;

    const form = new FormData();
    form.append("avatar", file);

    const config = {
      method: "PUT",
      body: form,
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data && data.success && data.data && data.data.user) {
        this.user = data.data.user;
        localStorage.setItem("user", JSON.stringify(this.user));
        // Refresh permissions (best-effort)
        try {
          await this.fetchPermissions();
        } catch (e) {
          /* ignore */
        }
        this._notifyUserUpdated();
        return { success: true, user: this.user };
      }
      return {
        success: false,
        error: (data && (data.error || data.message)) || "Upload failed",
      };
    } catch (err) {
      console.error("uploadAvatar error:", err);
      return { success: false, error: err.message || "Upload failed" };
    }
  }

  // Check of huidige gebruiker een specifieke permissie heeft
  hasPermission(permission) {
    if (!permission) return false;
    if (this.permissions && this.permissions.has(permission)) return true;
    // If permissions not loaded, be conservative and return false
    return false;
  }

  // Centralized helper: can current user view the Influencers area?
  // Logic: permission 'VIEW_INFLUENCERS' -> allowed
  // admin -> allowed
  // roles manager, employee, intern (stagair/stagiair) within department 'Influencer Marketing' -> allowed
  canViewInfluencers() {
    try {
      if (
        typeof this.hasPermission === "function" &&
        this.hasPermission("VIEW_INFLUENCERS")
      )
        return true;
    } catch (err) {
      // ignore permission lookup errors
    }

    if (this.isAdmin && this.isAdmin()) return true;

    const u = this.user;
    if (!u || !u.role) return false;

    const normalizeRole = (roleVal) => {
      if (!roleVal) return "";
      const r = String(roleVal).toLowerCase();
      if (r === "admin") return "administrator";
      if (r === "user" || r === "medewerker" || r === "employee")
        return "employee";
      if (r === "manager") return "manager";
      if (r === "intern" || r === "stagair" || r === "stagiair")
        return "intern";
      return r;
    };

    const userRole = normalizeRole(u.role);
    const allowedRoles = ["manager", "employee", "intern"];
    if (!allowedRoles.includes(userRole)) return false;

    const dept = u.department;
    const deptName = dept && typeof dept === "object" ? dept.name : dept;
    return (
      typeof deptName === "string" &&
      deptName.trim().toLowerCase() === "influencer marketing"
    );
  }

  // Centralized helper: can current user view the Artists prospecting area?
  // Logic: permission 'VIEW_ARTISTS' -> allowed
  // admin -> allowed
  // roles manager, employee, intern within department 'Studio' -> allowed
  canViewArtists() {
    try {
      if (
        typeof this.hasPermission === "function" &&
        this.hasPermission("VIEW_ARTISTS")
      )
        return true;
    } catch (err) {
      // ignore permission lookup errors
    }

    if (this.isAdmin && this.isAdmin()) return true;

    const u = this.user;
    if (!u || !u.role) return false;

    const normalizeRole = (roleVal) => {
      if (!roleVal) return "";
      const r = String(roleVal).toLowerCase();
      if (r === "admin") return "administrator";
      if (r === "user" || r === "medewerker" || r === "employee")
        return "employee";
      if (r === "manager") return "manager";
      if (r === "intern" || r === "stagair" || r === "stagiair")
        return "intern";
      return r;
    };

    const userRole = normalizeRole(u.role);
    const allowedRoles = ["manager", "employee", "intern"];
    if (!allowedRoles.includes(userRole)) return false;

    const dept = u.department;
    const deptName = dept && typeof dept === "object" ? dept.name : dept;
    return (
      typeof deptName === "string" &&
      deptName.trim().toLowerCase() === "studio"
    );
  }

  // Centralized helper: can current user view the Prospectie area?
  // Logic: permission 'VIEW_PROSPECTIE' -> allowed
  // admin -> allowed
  // managers in departments Sales/Management/Marketing -> allowed
  canViewProspectie() {
    try {
      if (
        typeof this.hasPermission === "function" &&
        this.hasPermission("VIEW_PROSPECTIE")
      )
        return true;
    } catch (err) {
      // ignore
    }

    if (this.isAdmin && this.isAdmin()) return true;

    // Allow manager, employee, or intern roles (normalized) in the allowed departments
    const hasAllowedRole =
      this.hasRole &&
      (this.hasRole("manager") ||
        this.hasRole("employee") ||
        this.hasRole("intern"));
    if (!hasAllowedRole) return false;

    const u = this.user;
    const dept = u && u.department;
    const deptName = dept && typeof dept === "object" ? dept.name : dept;
    if (typeof deptName !== "string") return false;

    const allowed = ["sales", "management", "marketing"];
    return allowed.includes(deptName.trim().toLowerCase());
  }

  // Login met Google idToken
  async loginWithGoogle(idToken) {
    try {
      const response = await ApiService.request("/auth/google", {
        method: "POST",
        body: JSON.stringify({ idToken }),
      });

      if (response.success) {
        this.token = response.data.token;
        this.user = response.data.user;
        this.isAuthenticated = true;

        localStorage.setItem("authToken", this.token);
        localStorage.setItem("user", JSON.stringify(this.user));

        // Save userId for agenda API calls (MongoDB uses _id)
        const userId = this.user._id || this.user.id;
        if (userId) {
          localStorage.setItem("userId", userId);
        }

        ApiService.setAuthToken(this.token);

        this._notifyUserUpdated();

        return { success: true, user: this.user };
      }

      // Backend may return the error message in either `error` or `message`.
      const errMsg =
        response.error || response.message || "Google login failed";
      return { success: false, error: errMsg };
    } catch (error) {
      return { success: false, error: error.message || "Google login failed" };
    }
  }

  // Forgot password (placeholder - backend moet uitgebreid worden)
  async forgotPassword(email) {
    try {
      // Simuleer API call (backend heeft dit endpoint nog niet)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return {
        success: true,
        message: "Reset link verstuurd (gesimuleerd)",
      };
    } catch (error) {
      return {
        success: false,
        error: "Reset link versturen gefaald",
      };
    }
  }
}

// Singleton instance
const authService = new AuthenticationService();
export default authService;
